import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { cache } from '@sama/rxjs-core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { AbstractShapeComponent } from '../abstract-shape.component';
import { calculateCuboidVertices } from './cuboid-solver';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- svg needs attribute selectors
  selector: '[h3Cuboid]',
  templateUrl: './cuboid.component.html',
  styleUrls: ['./cuboid.component.scss', '../../shape-parts/outline-host.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CuboidComponent extends AbstractShapeComponent implements OnInit {
  backgroundEdgeEndpoints$!: Observable<Array<[Point2D, Point2D]>>;
  foregroundEdgeEndpoints$!: Observable<Array<[Point2D, Point2D]>>;
  topFacePoints$!: Observable<Point2D[]>;
  sideFacePoints$!: Observable<Point2D[]>;
  primaryFacePoints$!: Observable<Point2D[]>;

  /**
   * The cuboid vertex order is defined as:
   * 0 - the closest bottom point of the front face
   * 1 - the closest top point of the front face
   * 2 - the furthest bottom point of the front face
   * 3 - the furthest top point of the front face
   * 4 - the closest bottom point of the back face
   * 5 - the closest top point of the back face
   * 6 - the furthest bottom point of the back face
   * 7 - the furthest top point of the back face
   */
  override ngOnInit() {
    super.ngOnInit();

    const vertices$ = this.points$.pipe(map(calculateCuboidVertices), cache());

    // Note: We depend on the specific order points are specified in below.
    this.backgroundEdgeEndpoints$ = edgeEndpoints$(vertices$, [
      [6, 2],
      [6, 4],
      [6, 7],
    ]);
    this.foregroundEdgeEndpoints$ = edgeEndpoints$(vertices$, [
      [0, 1],
      [0, 2],
      [0, 4],
      [3, 1],
      [3, 2],
      [3, 7],
      [5, 1],
      [5, 4],
      [5, 7],
    ]);
    this.primaryFacePoints$ = facePoints$(vertices$, [3, 2, 0, 1]);
    this.topFacePoints$ = facePoints$(vertices$, [7, 5, 1, 3]);
    this.sideFacePoints$ = facePoints$(vertices$, [5, 4, 0, 1]);
  }

  protected override buildAllSubpointInfo$() {
    return combineLatest([
      super.buildAllSubpointInfo$(),
      this.isSelected$,
    ]).pipe(
      map(([allBaseSubpointInfo, selected]) =>
        allBaseSubpointInfo.map((baseSubpointInfo, i) => ({
          ...baseSubpointInfo,
          hidden: i === 0 && !selected,
        })),
      ),
    );
  }
}

function edgeEndpoints$(
  vertices$: Observable<Array<Point2D | undefined>>,
  edgeIndexes: Array<[number, number]>,
) {
  return vertices$.pipe(
    map(
      (vertices) =>
        edgeIndexes
          .map((edge) => [vertices[edge[0]], vertices[edge[1]]])
          .filter((edge) => edge[0] && edge[1]) as Array<[Point2D, Point2D]>,
    ),
  );
}

function facePoints$(
  vertices$: Observable<Array<Point2D | undefined>>,
  indexes: [number, number, number, number],
) {
  return vertices$.pipe(
    map((vertices) => indexes.map((i) => vertices[i]).filter(isDefined)),
  );
}
